import { useEffect, useRef, useState } from 'react'
import {
	parseSafeZone,
	serializeSafeZone,
	type SafeZone,
} from '#app/domains/program/pure'
import { type PreparedImage } from '#app/utils/image.client.ts'
import { ImageCropper, type CropConfig } from './ImageCropper.tsx'
import { SafeZoneEditor, type SafeZoneFrame } from './SafeZoneEditor.tsx'

/**
 * Form image field: preview of the current image, a picker that opens the
 * safe-zone cropper, and a remove toggle. The prepared (cropped/compressed)
 * file is written into an uncontrolled file input so the surrounding
 * <form encType="multipart/form-data"> submits it with the rest of the fields.
 */
export function ImageField({
	name,
	initialImageUrl,
	label = 'Bilete',
	crop,
	helpText,
	safeZone,
}: {
	name: string
	initialImageUrl?: string | null
	/** null when a surrounding fieldset's legend already names the image */
	label?: string | null
	/** aspect/circle config for the cropper (null = free, no crop) */
	crop: CropConfig
	helpText?: string
	/**
	 * Let the editor mark a safe zone. Submitted as `${name}SafeZone` (JSON);
	 * `frames` are the crops the previews show.
	 */
	safeZone?: { initial?: string | null; frames: SafeZoneFrame[] }
}) {
	const [zone, setZone] = useState<SafeZone | null>(() =>
		parseSafeZone(safeZone?.initial),
	)
	const [zoneOpen, setZoneOpen] = useState(false)
	const [prepared, setPrepared] = useState<PreparedImage | null>(null)
	const [removed, setRemoved] = useState(false)
	const [modalOpen, setModalOpen] = useState(false)
	const [previewUrl, setPreviewUrl] = useState<string | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (!prepared) {
			setPreviewUrl(null)
			return
		}
		const url = URL.createObjectURL(prepared.file)
		setPreviewUrl(url)
		return () => URL.revokeObjectURL(url)
	}, [prepared])

	// push the blob into the file input so the normal form submit carries it
	useEffect(() => {
		const input = fileInputRef.current
		if (!input) return
		const dataTransfer = new DataTransfer()
		if (prepared) dataTransfer.items.add(prepared.file)
		input.files = dataTransfer.files
	}, [prepared])

	const currentUrl = prepared ? previewUrl : (initialImageUrl ?? undefined)

	return (
		<div className="form-control">
			{label ? (
				<label className="label">
					<span className="label-text">{label}</span>
				</label>
			) : null}
			<div className="flex items-center gap-3">
				{currentUrl ? (
					<img
						src={currentUrl}
						alt="Noverande bilete"
						className="h-20 w-20 rounded-xl object-cover"
					/>
				) : (
					<div className="bg-base-200 text-base-content/40 grid h-20 w-20 place-items-center rounded-xl text-2xl">
						🖼️
					</div>
				)}
				<div className="flex min-w-0 flex-1 flex-col gap-2">
					<div className="flex flex-wrap gap-2">
						<button
							type="button"
							className="btn btn-sm"
							onClick={() => setModalOpen(true)}
						>
							{currentUrl ? 'Endre bilete' : 'Vel bilete'}
						</button>
						{currentUrl && safeZone ? (
							<button
								type="button"
								className="btn btn-ghost btn-sm"
								onClick={() => setZoneOpen((open) => !open)}
							>
								{zone ? 'Trygg sone ✓' : 'Trygg sone'}
							</button>
						) : null}
						{currentUrl && (
							<button
								type="button"
								className="btn btn-ghost btn-sm text-error"
								onClick={() => {
									setRemoved(true)
									setPrepared(null)
									setZone(null)
									setZoneOpen(false)
								}}
							>
								Fjern
							</button>
						)}
					</div>
					{helpText && (
						<p className="text-base-content/70 text-sm">{helpText}</p>
					)}
				</div>
			</div>
			{safeZone && currentUrl && zoneOpen ? (
				<div className="mt-3">
					<SafeZoneEditor
						src={currentUrl}
						value={zone}
						frames={safeZone.frames}
						onChange={setZone}
					/>
				</div>
			) : null}
			{safeZone ? (
				<input
					type="hidden"
					name={`${name}SafeZone`}
					value={currentUrl ? (serializeSafeZone(zone) ?? '') : ''}
				/>
			) : null}
			{/* upload slot (empty unless the user picked an image above) */}
			<input ref={fileInputRef} type="file" name={name} className="hidden" />
			{/* force the input to upload empty/broken files if none chosen */}
			{prepared ? null : <input type="hidden" name={name} value="" />}
			{removed && <input type="hidden" name="removeImage" value="yes" />}

			{modalOpen && (
				<ImageCropper
					crop={crop}
					onClose={() => setModalOpen(false)}
					onConfirm={(result) => {
						setRemoved(false)
						setZone(null) // a different picture: the old zone no longer applies
						setPrepared(result)
						setModalOpen(false)
					}}
				/>
			)}
		</div>
	)
}
